<script lang="ts" setup>
import { AppButton, AppPassword, AppPasswordCriteria, AppTextField, AppTypography } from '#core/components';
import type { FormErrors } from '#core/types/Form';
import { useVuelidate } from '@vuelidate/core';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useAuthStore } from '../store/auth';
import type { RegisterForm } from '../types/Auth';

interface CompanyCheckResponse {
  data: {
    company_name?: string;
  };
}

const { validations } = useValidation();
const route = useRoute();
const router = useRouter();

const emailParam = computed(() => {
  const email = route.query.email as string;
  // Some URL parsers treat literal '+' as space (form encoding), so restore it
  return email ? email.replace(/ /g, '+') : '';
});

const {
  pending: loadingCompanyInfo,
  data: companyInfoResponse,
  error: companyInfoError,
  execute: executeCompanyCheck,
} = useSubmit<CompanyCheckResponse>();

const companyInfo = computed(() => companyInfoResponse.value?.data);

const fetchCompanyInfo = async (email: string) => {
  if (!email) return;

  await executeCompanyCheck('/auth/check-registration-email', {
    method: 'POST',
    body: { email },
  });
};

watch(emailParam, (newEmail) => {
  if (newEmail) {
    form.value.email = newEmail;
    fetchCompanyInfo(newEmail);
  }
});

onMounted(() => {
  if (emailParam.value) {
    form.value.email = emailParam.value;
    fetchCompanyInfo(emailParam.value);
  }
});

const title = computed(() => {
  if (emailParam.value && companyInfo.value?.company_name) {
    return `Welcome ${companyInfo.value.company_name}!`;
  }
  return 'Welcome!';
});

const form = ref<RegisterForm>({
  email: emailParam.value,
  password: '',
  password_confirmation: '',
});
const rules = computed(() => ({
  email: {
    required: validations.required('Email'),
    email: validations.email(),
  },
  password: {
    required: validations.required('Password'),
    minLength: validations.minLength('Password', 8),
    password: validations.password(),
  },
  password_confirmation: {
    required: validations.required('Password Confirmation'),
    sameAs: validations.sameAs('Password Confirmation', 'Password', form.value.password),
  },
}));

const description = computed(() => {
  if (emailParam.value) {
    return `Create a password to use when logging into your account`;
  }
  return 'Enter your email and password to create your account.';
});

const { user } = storeToRefs(useAuthStore());
const v$ = useVuelidate(rules, form, { $autoDirty: true });

const { token, initToken } = await useGenerateReCaptcha('register');
const { setToken } = useAuthState();
const { getSession } = useAuth();
const { data, pending: registerLoading, execute: execSubmit } = useSubmit();

const registerError = ref<{ data: FormErrors<RegisterForm> }>({ data: { errors: {}, message: '' } });
const handleSignUp = async (): Promise<void> => {
  const result = await v$.value.$validate();
  if (!result) return;
  try {
    registerLoading.value = true;
    await execSubmit('auth/register', {
      method: 'POST',
      body: {
        email: form.value.email,
        password: form.value.password,
        password_confirmation: form.value.password_confirmation,
      },
      headers: {
        ...(token.value && { 'X-Recaptcha-Token': token.value }),
      },
    });

    if (data.value) {
      setToken(data.value.data.access_token);
      await getSession();
      user.value = data.value.data.user;
      router.push({ name: 'verify' });
    }
  } catch (error) {
    registerError.value = error as { data: FormErrors<RegisterForm> };
    initToken();
  } finally {
    registerLoading.value = false;
  }
};

const errors = computed(() => {
  return $parsedError<RegisterForm>(form.value, v$, registerError.value?.data?.errors);
});
</script>

<template>
  <v-form @submit.prevent="handleSignUp" class="d-flex flex-column gap-2xl">
    <div class="d-flex flex-column gap-xs align-center">
      <AppTypography element="h4" class="font-h4 text-center">{{ title }}</AppTypography>
      <AppTypography element="p" class="text-center">{{ description }}</AppTypography>
    </div>

    <div class="d-flex flex-column gap-xl">
      <AppTextField
        v-model="form.email"
        data-cy="input-email"
        :error-messages="errors.email"
        :disabled="registerLoading"
        label="Email address"
        data-testid="email"
        autocomplete="email"
        hideDetails="auto"
      >
      </AppTextField>

      <AppPassword
        v-model="form.password"
        data-cy="input-password"
        label="Password"
        :error-messages="errors.password"
        :disabled="registerLoading"
        data-testid="password"
      >
      </AppPassword>

      <AppPassword
        v-model="form.password_confirmation"
        data-cy="input-password-confirmation"
        label="Confirm password"
        data-testid="password-confirmation"
        :error-messages="errors.password_confirmation"
        :disabled="registerLoading"
      >
      </AppPassword>

      <AppPasswordCriteria :password="form.password" />

      <AppButton
        data-cy="button-submit"
        block
        class="font-weight-bold"
        type="submit"
        data-testid="submit"
        :loading="registerLoading"
      >
        Continue
      </AppButton>

      <div class="divider-container d-flex align-center gap-xl" v-if="!emailParam">
        <div class="divider-line" />
        <AppTypography class="divider-text">Or</AppTypography>
        <div class="divider-line" />
      </div>
    </div>

    <AppTypography element="span" type="headline-2" class="d-block text-center" v-if="!emailParam">
      Already have an account?
      <nuxt-link to="/login" class="text-decoration-none font-body-bold ml-2" data-cy="sign-in">Login</nuxt-link>
    </AppTypography>

    <AppTypography element="span" class="text-center font-subheadline-regular text-secondary">
      By continuing, you agree to our
      <nuxt-link to="/terms-and-conditions" class="text-decoration-none font-subheadline-semibold text-primary"
        >Terms of Service</nuxt-link
      >
      and
      <nuxt-link to="/privacy-policy" class="text-decoration-none font-subheadline-semibold text-primary"
        >Privacy Policy.</nuxt-link
      >
    </AppTypography>
  </v-form>
</template>

<style lang="scss" scoped>
.divider-container {
  width: 100%;
}

.divider-line {
  flex: 1;
  height: 1px;
  background-color: rgb(var(--v-theme-border-default));
}

.divider-text {
  flex-shrink: 0;
  white-space: nowrap;
  color: rgb(var(--v-theme--text-tertiary));
}
</style>
